"use client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "@/app/components/providers/CurrencyProvider";
import { getFaqPriceParams } from "@/app/utils/currency-config";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  variant?: "default" | "career-page" | "free-ats";
  /** "signal" renders the editorial homepage treatment. */
  appearance?: "classic" | "signal";
}

export default function FAQAccordion({
  variant = "default",
  appearance = "classic",
}: FAQAccordionProps) {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set(["faq1"]));

  // Price interpolation for FAQ answers that mention prices
  const faqInterpolation = getFaqPriceParams(currency);

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const faqItems: FAQItem[] =
    variant === "free-ats"
      ? [
          {
            id: "faq1",
            question: t("freeAts.faqAts.question1"),
            answer: t("freeAts.faqAts.answer1"),
          },
          {
            id: "faq2",
            question: t("freeAts.faqAts.question2"),
            answer: t("freeAts.faqAts.answer2"),
          },
          {
            id: "faq3",
            question: t("freeAts.faqAts.question3"),
            answer: t("freeAts.faqAts.answer3"),
          },
          {
            id: "faq4",
            question: t("freeAts.faqAts.question4"),
            answer: t("freeAts.faqAts.answer4"),
          },
          {
            id: "faq5",
            question: t("freeAts.faqAts.question5"),
            answer: t("freeAts.faqAts.answer5"),
          },
        ]
      : variant === "career-page"
        ? [
            {
              id: "faq1",
              question: t("careerPage.faqCareer.question1"),
              answer: t("careerPage.faqCareer.answer1"),
            },
            {
              id: "faq2",
              question: t("careerPage.faqCareer.question2"),
              answer: t("careerPage.faqCareer.answer2"),
            },
            {
              id: "faq3",
              question: t("careerPage.faqCareer.question3"),
              answer: t("careerPage.faqCareer.answer3"),
            },
            {
              id: "faq4",
              question: t("careerPage.faqCareer.question4"),
              answer: t("careerPage.faqCareer.answer4"),
            },
            {
              id: "faq5",
              question: t("careerPage.faqCareer.question5"),
              answer: t("careerPage.faqCareer.answer5"),
            },
            {
              id: "faq6",
              question: t("careerPage.faqCareer.question6"),
              answer: t("careerPage.faqCareer.answer6"),
            },
          ]
        : [
            {
              id: "faq1",
              question: t("faq.question15"),
              answer: t("faq.answer15"),
            },
            {
              id: "faq2",
              question: t("faq.question16"),
              answer: t("faq.answer16"),
            },
            {
              id: "faq3",
              question: t("faq.question1"),
              answer: t("faq.answer1"),
            },
            {
              id: "faq4",
              question: t("faq.question2"),
              answer: t("faq.answer2"),
            },
            {
              id: "faq5",
              question: t("faq.question3"),
              answer: t("faq.answer3"),
            },
            {
              id: "faq6",
              question: t("faq.question4"),
              answer: t("faq.answer4"),
            },
            {
              id: "faq7",
              question: t("faq.question5"),
              answer: t("faq.answer5", faqInterpolation),
            },
            {
              id: "faq8",
              question: t("faq.question6"),
              answer: t("faq.answer6"),
            },
            {
              id: "faq9",
              question: t("faq.question7"),
              answer: t("faq.answer7"),
            },
            {
              id: "faq10",
              question: t("faq.question8"),
              answer: t("faq.answer8"),
            },
            {
              id: "faq11",
              question: t("faq.question9"),
              answer: t("faq.answer9"),
            },
            {
              id: "faq12",
              question: t("faq.question10"),
              answer: t("faq.answer10"),
            },
            {
              id: "faq13",
              question: t("faq.question11"),
              answer: t("faq.answer11"),
            },
            {
              id: "faq14",
              question: t("faq.question12"),
              answer: t("faq.answer12"),
            },
            {
              id: "faq15",
              question: t("faq.question13"),
              answer: t("faq.answer13"),
            },
            {
              id: "faq16",
              question: t("faq.question14"),
              answer: t("faq.answer14"),
            },
          ];

  const title =
    variant === "free-ats"
      ? t("freeAts.faqAts.title")
      : variant === "career-page"
        ? t("careerPage.faqCareer.title")
        : t("faq.title");
  const subtitle =
    variant === "free-ats"
      ? t("freeAts.faqAts.subtitle")
      : variant === "career-page"
        ? t("careerPage.faqCareer.subtitle")
        : t("faq.subtitle");

  if (appearance === "signal") {
    return (
      <section
        data-appearance="signal"
        className="relative w-full overflow-hidden py-20 lg:py-28"
      >
        <div className="relative mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-12 lg:px-8">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <h2 className="text-balance font-display text-[clamp(2rem,3.8vw,3.5rem)] font-semibold leading-[1.05] tracking-[-0.035em] text-signal-ink">
                {title}
              </h2>
              <span
                aria-hidden="true"
                className="mt-6 block h-1 w-24 rounded-full bg-signal-accent"
              />
              <p className="mt-6 max-w-[36ch] text-pretty text-[17px] leading-[1.65] text-signal-muted">
                {subtitle}
              </p>
            </div>
          </div>

          {/* Each question is its own capsule; answering it unfolds the
              capsule into a pod rather than opening a row in a table. */}
          <ul className="grid gap-3.5 lg:col-span-8">
            {faqItems.map((item, index) => {
              const isOpen = openItems.has(item.id);
              // Open and closed items share the clear glass: the lit number
              // disc, the answer and the turned plus mark the open one.
              return (
                <li key={item.id} className="glass rounded-[26px]">
                  <button
                    type="button"
                    onClick={() => toggleItem(item.id)}
                    aria-expanded={isOpen}
                    aria-controls={`${item.id}-answer`}
                    className="group flex w-full items-center gap-4 rounded-full py-3.5 pl-3.5 pr-5 text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-signal-accent/30 sm:pr-6"
                  >
                    <span
                      aria-hidden="true"
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-signal-mono text-[12px] transition-colors duration-300 ${
                        isOpen
                          ? "btn-glass-primary"
                          : "glass-inset text-signal-muted group-hover:text-signal-ink"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="flex-1 text-pretty font-display text-[17px] font-semibold leading-snug tracking-[-0.01em] text-signal-ink transition-colors duration-300 group-hover:text-signal-deep">
                      {item.question}
                    </h3>
                    <span
                      aria-hidden="true"
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                        isOpen
                          ? "glass-inset text-signal-deep"
                          : "glass-inset text-signal-ink group-hover:text-signal-deep"
                      }`}
                    >
                      <svg
                        data-testid="faq-toggle-icon"
                        viewBox="0 0 16 16"
                        fill="none"
                        className={`h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                          isOpen ? "rotate-45" : ""
                        }`}
                      >
                        <path
                          d="M8 2.5v11M2.5 8h11"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    id={`${item.id}-answer`}
                    hidden={!isOpen}
                    className="px-6 pb-7 pl-[calc(0.875rem+2.75rem+1rem)] pr-8"
                  >
                    <p
                      className="max-w-[64ch] text-pretty text-[15px] leading-[1.7] text-signal-muted"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full py-10 sm:py-20">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-signal-ink mb-4 sm:mb-6">
            {title}
          </h2>
          <p className="text-base sm:text-lg text-signal-muted max-w-2xl mx-auto pb-5">
            {subtitle}
          </p>
        </div>

        <div className="space-y-4">
          {faqItems.map(item => (
            <div
              key={item.id}
              className="glass rounded-glassInner overflow-hidden"
            >
              <button
                onClick={() => toggleItem(item.id)}
                className="w-full px-6 py-5 text-left flex justify-between items-center rounded-glassInner hover:bg-white/30 transition-colors duration-200"
                aria-expanded={openItems.has(item.id)}
              >
                <h3 className="text-lg font-semibold text-signal-ink pr-4">
                  {item.question}
                </h3>
                <div className="flex-shrink-0">
                  <svg
                    className={`w-6 h-6 text-signal-accent transition-transform duration-200 ${
                      openItems.has(item.id) ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {openItems.has(item.id) && (
                <div className="px-6 pb-5">
                  <div className="border-t border-signal-ink/10 pt-4">
                    <p
                      className="text-signal-muted leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: item.answer }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
